import { DriverSocketMsgType, type DriverSocketMsg } from '@libs/types/message'
import { DriverService } from './driver'
import type { Config, ConfigRepository, ConfigRepositoryResult } from './config_repository'
import type { VK } from '@/define/define'

export class LocalConfigRepository implements ConfigRepository {
  async list(): Promise<ConfigRepositoryResult> {
    const config = await DriverService.instance.askType<Config>(DriverSocketMsgType.MTCfgList)
    return { config }
  }

  async change(index: number): Promise<ConfigRepositoryResult> {
    return await this.postConfig({
      Type: DriverSocketMsgType.MTCfgChange,
      Data: { Value: index },
    } as DriverSocketMsg<{ Value: number }>)
  }

  async create(name: string): Promise<ConfigRepositoryResult> {
    return await this.postConfig({
      Type: DriverSocketMsgType.MTCfgCreate,
      Data: { Value: name },
    } as DriverSocketMsg<{ Value: string }>)
  }

  async delete(index: number): Promise<ConfigRepositoryResult> {
    return await this.postConfig({
      Type: DriverSocketMsgType.MTCfgDelete,
      Data: { Value: index },
    } as DriverSocketMsg<{ Value: number }>)
  }

  async rename(index: number, name: string): Promise<ConfigRepositoryResult> {
    return await this.postConfig({
      Type: DriverSocketMsgType.MTCfgRename,
      Data: { ID: index, Name: name },
    } as DriverSocketMsg<{ ID: number; Name: string }>)
  }

  async bindShortcut(index: number, name: string, shortcut: VK[]): Promise<ConfigRepositoryResult> {
    return await this.postConfig({
      Type: DriverSocketMsgType.MTCfgBindShortcut,
      Data: { ID: index, Name: name, Shortcut: shortcut },
    } as DriverSocketMsg<{ ID: number; Name: string; Shortcut: VK[] }>)
  }

  async import(name: string, dataBase64: string): Promise<ConfigRepositoryResult> {
    return await this.postConfig({
      Type: DriverSocketMsgType.MTCfgImport,
      Data: {
        Name: name,
        DataBase64: dataBase64,
      },
    } as DriverSocketMsg<{ Name: string; DataBase64: string }>)
  }

  async importReplace(name: string, dataBase64: string, replaceIndex: number): Promise<ConfigRepositoryResult> {
    return await this.postConfig({
      Type: DriverSocketMsgType.MTCfgImportReplace,
      Data: {
        Name: name,
        DataBase64: dataBase64,
        ReplaceIndex: replaceIndex,
      },
    } as DriverSocketMsg<{ Name: string; DataBase64: string; ReplaceIndex: number }>)
  }

  async export(index: number): Promise<string> {
    return await DriverService.instance.askTypeAndData<string>({
      Type: DriverSocketMsgType.MTCfgExport,
      Data: { Value: index },
    } as DriverSocketMsg<{ Value: number }>)
  }

  protected async postConfig(data: DriverSocketMsg<unknown>): Promise<ConfigRepositoryResult> {
    const config = await DriverService.instance.askTypeAndData<Config>(data)
    return { config }
  }
}
